/**
 * The Meta Pixel, so ads can be measured on what visitors do, not on clicks.
 *
 * Without it Meta only sees the click on the ad and keeps buying more people
 * who click. With it Meta sees the page load (its "landing page view") and the
 * tap on Google Play or the web app (a standard `Lead`), and a campaign can be
 * set to optimise for those instead.
 *
 * The Pixel id is public by design, like the PostHog key: it ships to every
 * visitor.
 *
 * Nothing about a gym, its members or a signed-in owner is sent from here.
 * This file runs on the marketing site only; the console never loads it.
 */
const PRODUCTION_PIXEL_ID = "1135310819440349";

// Set but empty turns the Pixel off, so a preview deploy can opt out.
const override = process.env.REACT_APP_META_PIXEL_ID;
const pixelId = override === undefined ? PRODUCTION_PIXEL_ID : override.trim();

const LIVE_HOSTS = ["varzishmate.com", "www.varzishmate.com"];

const SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";

/**
 * Meta's own loader stub: `fbq` queues calls until fbevents.js arrives and
 * replays them. Written out rather than pasted as an inline snippet so the
 * CSP needs no new hash.
 */
function installStub(win) {
  if (win.fbq) return;
  const fbq = function (...args) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  win.fbq = fbq;
  if (!win._fbq) win._fbq = fbq;
}

export const pixelEnabled = () => Boolean(pixelId);

export const initMetaPixel = (win = typeof window === "undefined" ? null : window) => {
  if (!pixelId || !win) return;
  // The prerender's headless browser is not a visitor.
  if (win.navigator.webdriver) return;
  // Only the live site: a dev server or a preview deploy would count as ad
  // traffic and train the ads on it. An explicit id override is a test, so
  // it runs anywhere.
  if (override === undefined && !LIVE_HOSTS.includes(win.location.hostname)) return;
  if (win.fbq) return;

  installStub(win);
  // fbevents.js sends a PageView itself on every client-side route change,
  // so only the first one is sent here.
  win.fbq("init", pixelId);
  win.fbq("track", "PageView");

  // Started at once, unlike PostHog: Meta counts a landing page view only if
  // the Pixel reports before the visitor leaves, and ad visitors on a slow
  // phone leave fast. Async and low priority, so the hero still paints first.
  const script = win.document.createElement("script");
  script.async = true;
  script.src = SCRIPT_SRC;
  script.fetchPriority = "low";
  win.document.head.appendChild(script);
};

/** A standard Meta event. Best effort: tracking must never break a click. */
export const trackPixel = (event, params) => {
  try {
    if (!pixelId || typeof window === "undefined" || !window.fbq) return;
    window.fbq("track", event, params);
  } catch {
    /* swallow */
  }
};
